/**
 * WorkflowEditor - Main React Flow workflow editor component
 * Integrates all workflow sub-components
 */

import { useCallback, useRef, useState, DragEvent } from 'react';
import { ReactFlow, Background, Controls, MiniMap, Panel, type ReactFlowInstance, BackgroundVariant, ConnectionLineType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useNodeTypes } from '../nodes';
import { useWorkflowStore, useWorkflowHandlers, useWorkflowSelection, useWorkflowPalette } from '../../../../stores/workflow';
import type { WorkflowNode, WorkflowEdge, WorkflowNodeData, WorkflowDefinition } from '../../../../stores/workflow/types';
import { WorkflowNodePalette } from './WorkflowNodePalette';
import { WorkflowControls } from './WorkflowControls';
import { NodeConfigPanel } from '../panels/NodeConfigPanel';
import { WorkflowMetadataPanel } from '../panels/WorkflowMetadataPanel';
import type { SupportedLanguage } from './defaultWorkflow';

interface WorkflowEditorProps {
  onSave?: (workflow: WorkflowDefinition) => Promise<void>;
  className?: string;
  /** When true, disables adding/removing nodes - only prompt editing is allowed */
  isReadOnlyStructure?: boolean;
  /** Language code for prompts - used by WorkflowMetadataPanel for language-specific placeholders */
  languageCode?: SupportedLanguage;
}

export const WorkflowEditor = ({ onSave, className = '', isReadOnlyStructure = true, languageCode = 'vi' }: WorkflowEditorProps) => {
  const reactFlowInstance = useRef<ReactFlowInstance<WorkflowNode, WorkflowEdge> | null>(null);
  const nodeTypes = useNodeTypes();

  // State for metadata panel
  const [isMetadataPanelOpen, setIsMetadataPanelOpen] = useState(false);

  // Store selectors
  const nodes = useWorkflowStore((s) => s.nodes);
  const edges = useWorkflowStore((s) => s.edges);
  const addNode = useWorkflowStore((s) => s.addNode);
  const { onNodesChange, onEdgesChange, onConnect } = useWorkflowHandlers();
  const { selectedNodeId, setSelectedNodeId, clearSelection } = useWorkflowSelection();
  const { isDragging, setDraggedNodeType } = useWorkflowPalette();

  // Toggle metadata panel (closes node panel when opening)
  const handleToggleMetadataPanel = useCallback(() => {
    setIsMetadataPanelOpen((prev) => !prev);
    if (!isMetadataPanelOpen) {
      clearSelection(); // Close node config panel when opening metadata panel
    }
  }, [isMetadataPanelOpen, clearSelection]);

  // Handle node click
  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: WorkflowNode) => {
      setSelectedNodeId(node.id);
    },
    [setSelectedNodeId]
  );

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  // Handle drag over
  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop - create new node from palette
  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      const data = event.dataTransfer.getData('application/reactflow');
      if (!data || !reactFlowInstance.current) return;

      try {
        const { type, data: nodeData } = JSON.parse(data) as {
          type: string;
          data: Partial<WorkflowNodeData>;
        };

        // Get the position where the node was dropped
        const position = reactFlowInstance.current.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY
        });

        // Generate unique ID
        const newNodeId = `${type}-${Date.now()}`;

        // Create new node
        const newNode: WorkflowNode = {
          id: newNodeId,
          type,
          position,
          data: {
            ...nodeData,
            label: nodeData.label || type,
            isConfigured: nodeData.isConfigured ?? false
          } as WorkflowNodeData
        };

        addNode(newNode);
        setSelectedNodeId(newNodeId);
      } catch (error) {
        console.error('Failed to create node from drop:', error);
      }

      setDraggedNodeType(null);
    },
    [addNode, setSelectedNodeId, setDraggedNodeType]
  );

  // Handle React Flow init
  const onInit = useCallback((instance: ReactFlowInstance<WorkflowNode, WorkflowEdge>) => {
    reactFlowInstance.current = instance;
  }, []);

  return (
    <div className={`flex h-full w-full ${className}`}>
      {/* Left: Node Palette - hidden in read-only structure mode */}
      {!isReadOnlyStructure && <WorkflowNodePalette />}

      {/* Center: Canvas */}
      <div className={`relative flex-1 ${isDragging ? 'bg-primary/5' : ''}`}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onInit={onInit}
          onDragOver={isReadOnlyStructure ? undefined : onDragOver}
          onDrop={isReadOnlyStructure ? undefined : onDrop}
          nodeTypes={nodeTypes}
          fitView
          snapToGrid
          snapGrid={[15, 15]}
          // Performance optimizations
          minZoom={0.1}
          maxZoom={4}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          // Visual settings
          connectionLineType={ConnectionLineType.SmoothStep}
          deleteKeyCode={isReadOnlyStructure ? null : ['Backspace', 'Delete']}
          multiSelectionKeyCode={['Meta', 'Ctrl']}
          panOnScroll
          selectionOnDrag
          selectNodesOnDrag={false}
          nodesConnectable={!isReadOnlyStructure}
          edgesFocusable={!isReadOnlyStructure}
          // Edge styling
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: true,
            style: { strokeWidth: 2 }
          }}
        >
          {/* Background Grid */}
          <Background variant={BackgroundVariant.Dots} gap={15} size={1} color='#e5e7eb' className='dark:!bg-gray-900' />

          {/* Zoom Controls */}
          <Controls
            showZoom
            showFitView
            showInteractive
            className='!bg-white !shadow-lg dark:!bg-gray-800 [&>button]:!border-gray-200 [&>button]:dark:!border-gray-700 [&>button]:dark:!bg-gray-800 [&>button>svg]:dark:!fill-gray-300'
          />

          {/* Mini Map */}
          <MiniMap
            nodeStrokeWidth={3}
            zoomable
            pannable
            className='!bg-white !shadow-lg dark:!bg-gray-800 [&>svg]:dark:!bg-gray-800'
            nodeColor={(node) => {
              switch (node.type) {
                case 'start':
                  return '#22c55e';
                case 'end':
                  return '#ef4444';
                case 'agent':
                  return '#00D9FF';
                case 'tool':
                  return '#6366f1';
                case 'condition':
                  return '#f59e0b';
                default:
                  return '#6b7280';
              }
            }}
          />

          {/* Top Toolbar */}
          <Panel position='top-center'>
            <WorkflowControls onSave={onSave} />
          </Panel>

          {/* Metadata Panel Toggle */}
          <Panel position='top-right'>
            <button
              onClick={handleToggleMetadataPanel}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium shadow-lg transition-colors ${
                isMetadataPanelOpen
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-dark-background-secondary dark:text-dark-text-secondary dark:hover:bg-dark-background-hover'
              }`}
              title='Workflow Settings'
            >
              <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
                />
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
              </svg>
              <span className='hidden sm:inline'>Settings</span>
            </button>
          </Panel>

          {/* Drop indicator - only show when not in read-only mode */}
          {!isReadOnlyStructure && isDragging && (
            <Panel position='bottom-center' className='pointer-events-none'>
              <div className='rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white shadow-lg'>Drop to add node</div>
            </Panel>
          )}
        </ReactFlow>
      </div>

      {/* Right: Config Panel (Node config or Metadata panel) */}
      {selectedNodeId && !isMetadataPanelOpen && <NodeConfigPanel />}
      <WorkflowMetadataPanel isOpen={isMetadataPanelOpen} onClose={() => setIsMetadataPanelOpen(false)} languageCode={languageCode} />
    </div>
  );
};

export default WorkflowEditor;
